import React from "react";
import { Link } from "react-router-dom";
import { Heart, MessageCircle, Trash2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { toast } from "sonner";

export default function CommunityPostCard({ post, onChanged }) {
  const { user } = useAuth();
  const [liked, setLiked] = React.useState(post.liked || false);
  const [likes, setLikes] = React.useState(post.likes_count || 0);

  const toggleLike = async (e) => {
    e.preventDefault();
    if (!user) return toast.error("Sign in to like posts");
    try {
      const { data } = await api.post(`/community/posts/${post.id}/like`);
      setLiked(data.liked);
      setLikes(data.likes_count);
    } catch (_) {
      toast.error("Could not update like");
    }
  };

  const remove = async (e) => {
    e.preventDefault();
    if (!window.confirm("Delete this post?")) return;
    try {
      await api.delete(`/community/posts/${post.id}`);
      toast.success("Post deleted");
      onChanged && onChanged();
    } catch (_) {
      toast.error("Could not delete post");
    }
  };

  const canDelete = user && (user.id === post.author_id || user.role === "admin");

  return (
    <article
      data-testid={`community-post-${post.id}`}
      className="bg-[#0a0a0b] border border-white/10 p-6 hover:border-white/20 transition"
    >
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 bg-yellow-400 text-black font-bold flex items-center justify-center text-sm">
          {(post.author_name || "U")[0].toUpperCase()}
        </div>
        <div className="flex-1">
          <div className="text-sm font-medium text-white">{post.author_name}</div>
          <div className="text-xs font-mono text-zinc-500">
            {new Date(post.created_at).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" })}
          </div>
        </div>
        {canDelete && (
          <button
            onClick={remove}
            data-testid={`community-post-delete-${post.id}`}
            className="text-zinc-500 hover:text-red-400 transition"
            title="Delete post"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>

      <Link to={`/community/${post.id}`} className="block group">
        <h3 className="font-display font-bold text-xl text-white group-hover:text-yellow-400 transition leading-snug">
          {post.title}
        </h3>
        <p className="mt-3 text-zinc-300 leading-relaxed line-clamp-3">
          {post.content}
        </p>
      </Link>

      <div className="mt-5 pt-4 border-t border-white/5 flex items-center gap-5 text-sm">
        <button
          onClick={toggleLike}
          data-testid={`community-post-like-${post.id}`}
          className={`flex items-center gap-2 transition ${liked ? "text-red-400" : "text-zinc-400 hover:text-red-400"}`}
        >
          <Heart className={`w-4 h-4 ${liked ? "fill-current" : ""}`} />
          <span className="font-mono">{likes}</span>
        </button>
        <Link
          to={`/community/${post.id}`}
          data-testid={`community-post-comment-link-${post.id}`}
          className="flex items-center gap-2 text-zinc-400 hover:text-yellow-400 transition"
        >
          <MessageCircle className="w-4 h-4" />
          <span className="font-mono">{post.comments_count || 0}</span>
        </Link>
      </div>
    </article>
  );
}